import { useEffect, useMemo, useState, useSyncExternalStore } from 'react'
import { getProgressTasks, subscribeProgress, TaskView } from '@client/stores/progressStore'
import { refreshProgress, startProgressWatch } from '@client/stores/progressWatch'
import { connectSocketOnce } from '@client/socket/socket'
import { nextExpiry, queueByRow } from './convertQueue'

/** As many as the server hands out at once (routes/tasks). */
const PROJECT_TASK_LIMIT = 200

/**
 * The conversion queue of a project, by row of the converter list.
 *
 * Fed like every progress display (stores/progressWatch): socket events, and a
 * snapshot whenever the connection comes up — here the project's own, so that
 * a file waiting behind many others still finds its row.
 */
export default function useConvertQueue(projectId: string): Record<string, TaskView> {
    useEffect(() => startProgressWatch(), [])

    useEffect(() => {
        if (!projectId) return

        const refresh = () => void refreshProgress({ projectId, limit: PROJECT_TASK_LIMIT })
        const socket = connectSocketOnce()
        socket.on('connect', refresh)
        refresh()

        return () => {
            socket.off('connect', refresh)
        }
    }, [projectId])

    const tasks = useSyncExternalStore(subscribeProgress, getProgressTasks)

    // An ended row goes back to a plain one when the server drops its record;
    // nothing announces that, so the view looks again at that moment.
    const [now, setNow] = useState(() => Date.now())
    const rows = useMemo(() => queueByRow(tasks, projectId, now), [tasks, projectId, now])

    useEffect(() => {
        const wait = nextExpiry(rows, now)
        if (wait === null) return

        const timer = setTimeout(() => setNow(Date.now()), wait + 50)
        return () => clearTimeout(timer)
    }, [rows, now])

    return rows
}
